import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Star, ShieldCheck, Clock, Sparkles, UserCheck, Sofa, BedDouble, ChefHat, Bath, Building2, Home as HomeIcon, Tv } from "lucide-react";
import Marquee from "@/components/Marquee";
import Reveal from "@/components/Reveal";
import VideoEmbed from "@/components/VideoEmbed";
import ProjectCard from "@/components/ProjectCard";
import { featuredProjects } from "@/data/projects";
import { videoShowcase } from "@/data/videos";

const services = [
    { num: "01", title: "Living Room Design", icon: Sofa, desc: "Conversation pieces, considered materials, and lighting that performs from morning to night." },
    { num: "02", title: "Bedroom Design", icon: BedDouble, desc: "Quiet, layered sanctuaries built around custom joinery, soft palettes and curated textiles." },
    { num: "03", title: "Modular Kitchen", icon: ChefHat, desc: "German engineering, Italian stone, and chef-grade workflows tailored to your home." },
    { num: "04", title: "Bathroom Design", icon: Bath, desc: "Spa-grade fittings, full-slab stone, and hidden lighting — your everyday retreat." },
    { num: "05", title: "Commercial Interiors", icon: Building2, desc: "Hospitality-led workplaces, F&B, and retail spaces that elevate your brand." },
    { num: "06", title: "Full Home Renovation", icon: HomeIcon, desc: "End-to-end home transformation — from concept and 3D to handover with one team." },
    { num: "07", title: "Home Theatre Design", icon: Tv, desc: "Immersive private cinemas with acoustic engineering, 4K projection, Dolby Atmos sound and bespoke seating — built into your home." },
];

const stats = [
    { num: "200+", label: "Projects Delivered" },
    { num: "08+", label: "Years of Experience" },
    { num: "500+", label: "Happy Families" },
    { num: "15+", label: "Design Awards" },
];

const valueProps = [
    { num: "01", title: "End-to-End Execution", desc: "From concept to handover, one accountable team manages every craftsman, every material, every milestone.", icon: Sparkles },
    { num: "02", title: "Transparent Pricing", desc: "Itemised quotes, locked rates and zero hidden costs. You see exactly where every rupee goes.", icon: ShieldCheck },
    { num: "03", title: "On-Time Delivery", desc: "Master schedules with weekly check-ins. 96% of our projects deliver on or before the promised date.", icon: Clock },
    { num: "04", title: "Dedicated Design Manager", desc: "A single point of contact who owns your project — from your first sketch to your final walkthrough.", icon: UserCheck },
];

const processSteps = [
    { num: "01", title: "Consultation", desc: "We meet at your space or our studio, understand how you live, and define ambition with budget." },
    { num: "02", title: "Design Brief", desc: "Mood boards, finish palettes and a detailed brief — signed off before a single line is drawn." },
    { num: "03", title: "3D Visualisation", desc: "Photo-real 3D renders of every room. Iterate without surprises before site mobilisation." },
    { num: "04", title: "Execution", desc: "Workshop-built joinery, vendor management, daily site quality checks. You watch it come alive." },
    { num: "05", title: "Handover", desc: "Snag-free handover, finishing touches, and a 5-year structural warranty on all custom work." },
];

const testimonials = [
    { name: "Aarav & Meera Reddy", location: "Banjara Hills, Hyderabad", rating: 5, quote: "Midasis turned our duplex into something out of an Architectural Digest spread. Every craftsman they sent felt like an extension of the design team. They delivered three weeks early." },
    { name: "Shruti Khanna", location: "Jubilee Hills, Hyderabad", rating: 5, quote: "Transparent budgeting, weekly walkthroughs, zero surprises. The kitchen alone has redefined how our family eats together. Genuinely the best investment we've made into our home." },
    { name: "Rohan Mehta", location: "HITEC City, Hyderabad", rating: 5, quote: "We engaged Midasis for our 18,000 sq.ft headquarters. They delivered a workplace that doubles as a hospitality experience. Our retention numbers have improved measurably since." },
];

export default function Home() {
    return (
        <div data-testid="home-page" className="bg-charcoal text-cream">
            {/* HERO */}
            <section
                data-testid="hero-section"
                className="relative min-h-[100svh] flex items-end overflow-hidden bg-charcoal"
            >
                <div
                    className="hero-bg absolute inset-0"
                    style={{
                        backgroundImage:
                            "url('https://images.unsplash.com/photo-1618219740975-d40978bb7378?auto=format&fit=crop&w=2400&q=85')",
                    }}
                />
                <div
                    className="absolute inset-0"
                    style={{
                        background:
                            "linear-gradient(180deg, rgba(13,13,13,0.7) 0%, rgba(13,13,13,0.55) 30%, rgba(13,13,13,0.85) 75%, rgba(13,13,13,1) 100%)",
                    }}
                />
                <div
                    className="absolute inset-0"
                    style={{
                        background:
                            "linear-gradient(90deg, rgba(13,13,13,0.85) 0%, rgba(13,13,13,0.45) 50%, rgba(13,13,13,0.4) 100%)",
                    }}
                />
                <div className="noise-overlay" />
                <div className="relative container-pad mx-auto pb-20 md:pb-28 pt-32 w-full">
                    <p className="overline mb-8" data-testid="hero-overline">Hyderabad · Est. 2017</p>
                    <h1
                        data-testid="hero-headline"
                        className="font-display text-[44px] sm:text-6xl lg:text-[7.5rem] leading-[0.95] tracking-tighter text-cream max-w-6xl"
                    >
                        Where vision <br />
                        meets <em className="italic text-gold">elegance.</em>
                    </h1>
                    <p
                        data-testid="hero-subtitle"
                        className="mt-8 max-w-xl text-base md:text-lg text-[#D8D8D2] leading-relaxed"
                    >
                        Award-winning interior design for residences and spaces across Hyderabad —
                        crafted with precision, delivered with restraint.
                    </p>
                    <div className="mt-10 flex flex-col sm:flex-row gap-4">
                        <Link to="/projects" className="btn-primary" data-testid="hero-cta-explore">
                            Explore Projects <ArrowRight size={16} />
                        </Link>
                        <Link to="/contact" className="btn-secondary" data-testid="hero-cta-consult">
                            Book Consultation <ArrowUpRight size={16} />
                        </Link>
                    </div>
                </div>
            </section>

            <Marquee />

            {/* STATS */}
            <section data-testid="stats-section" className="bg-charcoal border-b border-[#262626]">
                <div className="container-pad mx-auto py-20 md:py-24 grid grid-cols-2 md:grid-cols-4 gap-12">
                    {stats.map((s, i) => (
                        <Reveal key={s.label} delay={i * 80}>
                            <div data-testid={`stat-${i}`} className="border-l border-[#262626] pl-6">
                                <p className="font-display text-5xl md:text-6xl text-cream leading-none tracking-tight">
                                    {s.num}
                                </p>
                                <p className="mt-4 text-xs uppercase tracking-[0.25em] text-[#A3A3A3]">
                                    {s.label}
                                </p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* SERVICES */}
            <section data-testid="services-section" className="container-pad mx-auto py-28 md:py-40">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-20">
                    <div className="lg:col-span-5">
                        <Reveal>
                            <p className="overline mb-6">— Services</p>
                            <h2 className="font-display text-5xl md:text-6xl leading-[1.05] tracking-tight text-cream">
                                Seven disciplines.<br /> One uncompromising standard.
                            </h2>
                        </Reveal>
                    </div>
                    <div className="lg:col-span-6 lg:col-start-7 flex items-end">
                        <Reveal delay={120}>
                            <p className="text-[#A3A3A3] text-base md:text-lg leading-relaxed max-w-md">
                                Every Midasis project is built around how you actually live and work.
                                We design, build, and deliver — across six focused practices.
                            </p>
                        </Reveal>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#262626] border border-[#262626]">
                    {services.map((s, i) => {
                        const Icon = s.icon;
                        return (
                            <Reveal key={s.num} delay={i * 60}>
                                <Link
                                    to="/services"
                                    data-testid={`service-card-${i}`}
                                    className="group block bg-charcoal h-full p-10 md:p-12 hover:bg-charcoal-2 transition-colors duration-500"
                                >
                                    <div className="flex items-start justify-between mb-10">
                                        <span className="section-number text-3xl">{s.num}</span>
                                        <Icon className="text-gold opacity-80 group-hover:opacity-100 transition-opacity" size={28} />
                                    </div>
                                    <h3 className="font-display text-3xl md:text-4xl text-cream tracking-tight leading-tight mb-4">
                                        {s.title}
                                    </h3>
                                    <p className="text-[#A3A3A3] text-sm leading-relaxed mb-8 max-w-sm">
                                        {s.desc}
                                    </p>
                                    <span className="inline-flex items-center gap-2 text-gold text-xs uppercase tracking-[0.25em] font-medium">
                                        Explore <ArrowRight size={14} />
                                    </span>
                                </Link>
                            </Reveal>
                        );
                    })}
                </div>
            </section>

            {/* FEATURED PROJECTS */}
            <section data-testid="featured-projects-section" className="bg-charcoal-2 py-28 md:py-40 border-t border-[#262626]">
                <div className="container-pad mx-auto">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
                        <div>
                            <Reveal>
                                <p className="overline mb-6">— Featured Projects</p>
                                <h2 className="font-display text-5xl md:text-6xl leading-[1.05] tracking-tight text-cream max-w-3xl">
                                    Spaces that <em className="italic text-gold">tell your story.</em>
                                </h2>
                            </Reveal>
                        </div>
                        <Link to="/projects" className="btn-secondary self-start md:self-end" data-testid="featured-view-all">
                            View All <ArrowRight size={16} />
                        </Link>
                    </div>

                    {/* YouTube walkthroughs — 6 curated */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-20">
                        {videoShowcase.map((v, i) => (
                            <Reveal key={v.videoId} delay={(i % 2) * 140}>
                                <VideoEmbed
                                    videoId={v.videoId}
                                    subtitle={v.subtitle}
                                    title={v.title}
                                    testId={`home-video-${i + 1}`}
                                />
                            </Reveal>
                        ))}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {featuredProjects.map((p, i) => (
                            <Reveal key={p.id} delay={i * 80}>
                                <Link to="/projects" data-testid={`featured-project-${p.id}`}>
                                    <ProjectCard project={p} index={i} />
                                </Link>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* WHY US */}
            <section data-testid="why-us-section" className="container-pad mx-auto py-28 md:py-40">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-20">
                    <div className="lg:col-span-6">
                        <Reveal>
                            <p className="overline mb-6">— Why Midasis</p>
                            <h2 className="font-display text-5xl md:text-6xl leading-[1.05] tracking-tight text-cream">
                                Timeless design.<br /> Flawless execution.
                            </h2>
                        </Reveal>
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#262626] border border-[#262626]">
                    {valueProps.map((v, i) => {
                        const Icon = v.icon;
                        return (
                            <Reveal key={v.num} delay={i * 80}>
                                <div data-testid={`value-prop-${i}`} className="bg-charcoal p-10 md:p-14 h-full">
                                    <div className="flex items-start justify-between mb-8">
                                        <span className="section-number text-3xl">{v.num}</span>
                                        <Icon className="text-gold" size={26} />
                                    </div>
                                    <h3 className="font-display text-3xl md:text-4xl text-cream mb-4 tracking-tight leading-tight">{v.title}</h3>
                                    <p className="text-[#A3A3A3] leading-relaxed max-w-md">{v.desc}</p>
                                </div>
                            </Reveal>
                        );
                    })}
                </div>
            </section>

            {/* PROCESS */}
            <section data-testid="process-section" className="bg-charcoal-2 border-t border-[#262626] py-28 md:py-40">
                <div className="container-pad mx-auto">
                    <Reveal>
                        <p className="overline mb-6">— Our Process</p>
                        <h2 className="font-display text-5xl md:text-6xl leading-[1.05] tracking-tight text-cream max-w-3xl mb-20">
                            Five steps. <em className="italic text-gold">Zero surprises.</em>
                        </h2>
                    </Reveal>
                    <div className="space-y-0">
                        {processSteps.map((step, i) => (
                            <Reveal key={step.num} delay={i * 60}>
                                <div data-testid={`process-step-${i}`} className="grid grid-cols-12 gap-6 py-10 border-t border-[#262626] items-baseline">
                                    <div className="col-span-12 md:col-span-2">
                                        <span className="section-number text-5xl md:text-6xl">{step.num}</span>
                                    </div>
                                    <div className="col-span-12 md:col-span-5">
                                        <h3 className="font-display text-3xl md:text-4xl text-cream tracking-tight">{step.title}</h3>
                                    </div>
                                    <div className="col-span-12 md:col-span-5">
                                        <p className="text-[#A3A3A3] leading-relaxed">{step.desc}</p>
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                        <div className="border-t border-[#262626]" />
                    </div>
                </div>
            </section>

            {/* TESTIMONIALS */}
            <section data-testid="testimonials-section" className="container-pad mx-auto py-28 md:py-40">
                <Reveal>
                    <p className="overline mb-6">— Clients</p>
                    <h2 className="font-display text-5xl md:text-6xl leading-[1.05] tracking-tight text-cream max-w-3xl mb-20">
                        Trusted by families <br />and Hyderabad's finest.
                    </h2>
                </Reveal>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {testimonials.map((t, i) => (
                        <Reveal key={t.name} delay={i * 100}>
                            <article data-testid={`testimonial-${i}`} className="bg-charcoal-2 border border-[#1c1c1c] p-10 h-full flex flex-col">
                                <div className="flex gap-1 mb-6">
                                    {Array.from({ length: t.rating }).map((_, j) => (
                                        <Star key={j} size={16} className="text-gold" fill="#C9A84C" />
                                    ))}
                                </div>
                                <p className="font-display italic text-xl md:text-2xl text-cream leading-snug flex-1">
                                    &ldquo;{t.quote}&rdquo;
                                </p>
                                <div className="mt-8 pt-6 border-t border-[#262626]">
                                    <p className="text-cream font-medium">{t.name}</p>
                                    <p className="text-xs text-[#A3A3A3] mt-1 uppercase tracking-widest">{t.location}</p>
                                </div>
                            </article>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* CTA BANNER */}
            <section data-testid="cta-banner" className="cta-banner py-28 md:py-40 relative overflow-hidden">
                <div className="container-pad mx-auto text-center">
                    <Reveal>
                        <p className="overline mb-8">— Begin</p>
                        <h2 className="font-display text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tighter text-cream max-w-5xl mx-auto">
                            Ready to transform <br /><em className="italic gold-gradient-text">your space?</em>
                        </h2>
                        <p className="mt-8 text-lg text-[#D8D8D2] max-w-xl mx-auto">
                            Get a complimentary design consultation today — no commitment, just clarity.
                        </p>
                        <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center">
                            <Link to="/contact" className="btn-primary" data-testid="cta-banner-button">
                                Talk to a Designer <ArrowRight size={16} />
                            </Link>
                            <a href="https://wa.me/919249899999" target="_blank" rel="noreferrer" className="btn-secondary" data-testid="cta-banner-whatsapp">
                                WhatsApp Us <ArrowUpRight size={16} />
                            </a>
                        </div>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
